import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Paper,
    Chip,
    IconButton,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    CircularProgress,
    Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import { SearchHistory } from '../../../models/SearchHistory';
import { getSearchHistoryById } from '../../../services/historyService';
import { getLeadsByBusinessIds } from '../../../services/leadService';

const HistoryDetailsComponent: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [history, setHistory] = useState<SearchHistory | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [leadStatus, setLeadStatus] = useState<Map<string, boolean>>(new Map());
    const [leadIds, setLeadIds] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        const loadHistoryDetails = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const data = await getSearchHistoryById(id);
                setHistory(data);

                // Get lead status for all businesses
                const businessIds = data.results.map(r => r.id);
                const leadMap = await getLeadsByBusinessIds(businessIds);

                // Create maps for both lead status and lead IDs
                const statusMap = new Map<string, boolean>();
                const idMap = new Map<string, string>();

                leadMap.forEach((lead, businessId) => {
                    statusMap.set(businessId, true);
                    idMap.set(businessId, lead.id);
                });

                setLeadStatus(statusMap);
                setLeadIds(idMap);
            } catch (error) {
                console.error('Error loading history details:', error);
                setError('Failed to load history details');
            } finally {
                setLoading(false);
            }
        };

        loadHistoryDetails();
    }, [id]);

    const handleBusinessClick = (businessId: string) => {
        navigate(`/businesses/${businessId}`);
    };

    const handleLeadClick = (businessId: string) => {
        const leadId = leadIds.get(businessId);
        if (leadId) {
            navigate(`/leads/lead/${leadId}`);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !history) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error || 'History not found'}
            </Alert>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => navigate('/history')} sx={{ mr: 2 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4">Search Results</Typography>
                </Box>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary">Search Query</Typography>
                            <Typography variant="h6">{history.query}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                            <Typography variant="h6">
                                {history.timestamp.toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary">Industries</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                {history.filters.industries.map((industry, index) => (
                                    <Chip key={index} label={industry} color="primary" variant="outlined" />
                                ))}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary">Locations</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                {history.filters.location.map((location, index) => (
                                    <Chip key={index} label={location} color="secondary" variant="outlined" />
                                ))}
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                <Typography variant="h5" sx={{ mb: 2 }}>
                    Results ({history.results.length}) - {Array.from(leadStatus.values()).filter(Boolean).length} Leads
                </Typography>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Business</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.results.map((result) => (
                                <TableRow
                                    key={result.id}
                                    onClick={() => handleBusinessClick(result.id)}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                >
                                    <TableCell>
                                        <Typography variant="subtitle1">{result.name}</Typography>
                                        {result.website && (
                                            <Button
                                                startIcon={<LanguageIcon />}
                                                href={result.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                size="small"
                                                sx={{ mt: 1 }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                Website
                                            </Button>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {result.phone && (
                                            <Button
                                                startIcon={<PhoneIcon />}
                                                href={`tel:${result.phone}`}
                                                size="small"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {result.phone}
                                            </Button>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{result.address}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={leadStatus.get(result.id) ? "Lead Created" : "No Lead"}
                                            color={leadStatus.get(result.id) ? "success" : "default"}
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (leadStatus.get(result.id)) {
                                                    handleLeadClick(result.id);
                                                }
                                            }}
                                            sx={leadStatus.get(result.id) ? {
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: '#2e7d32'
                                                }
                                            } : {}}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Container>
    );
};

export default HistoryDetailsComponent; 